import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { PlainDarkPanelless } from "survey-core/themes";
import "survey-core/survey-core.css";
import { Spin } from "../../../design-system";
import { handleError } from "../../../common/ErrorHandling";
import { getIntakeSurvey } from "../GuidedIntakeApi";
import type { SurveyResponse } from "../GuidedIntakeApi";
import classes from "../GuidedIntake.module.less";

export interface GuidedSurveyHandle {
    /** Validate the survey (shows inline errors); true when there are none. */
    validate: () => boolean;
}

interface Props {
    projectId: number;
    /** A previously-saved response to restore (resuming or re-entering). */
    initialResponse?: object;
    /**
     * Reports every edit as the full schema + response. The caller owns caching
     * and persistence — this component keeps no copy of its own.
     */
    onChange?: (survey: SurveyResponse) => void;
    /** Reports whether the survey is fully/valid-ly answered (drives submit gating). */
    onValidityChange?: (complete: boolean) => void;
}

/**
 * The guided-questions survey for the draft's analysis kind — the same
 * SurveyJS form and dark theme as the project wizard, shown in place of the
 * chat. Presentation only: every edit is reported up through `onChange`;
 * persistence lives with the caller (useGuidedIntake).
 */
export const GuidedSurvey = forwardRef<GuidedSurveyHandle, Props>(
    ({ projectId, initialResponse, onChange, onValidityChange }, ref) => {
        const [model, setModel] = useState<Model | null>(null);

        useEffect(() => {
            let cancelled = false;
            getIntakeSurvey(projectId)
                .then(({ schema: loadedSchema }) => {
                    if (cancelled) return;
                    const m = new Model(loadedSchema);
                    m.applyTheme({
                        ...PlainDarkPanelless,
                        cssVariables: {
                            ...PlainDarkPanelless.cssVariables,
                            "--sjs-base-unit": "6px",
                        },
                    });
                    m.showCompleteButton = false;
                    if (initialResponse) m.data = initialResponse;
                    m.onValueChanged.add(() => {
                        onValidityChange?.(m.validate(false, false));
                        onChange?.({ schema: loadedSchema, response: m.data });
                    });
                    setModel(m);
                    onValidityChange?.(m.validate(false, false));
                })
                .catch(handleError);
            return () => {
                cancelled = true;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [projectId]);

        useImperativeHandle(
            ref,
            () => ({
                validate: () => (model ? model.validate(true, true) : false),
            }),
            [model]
        );

        if (!model) {
            return (
                <div className={classes.startingState}>
                    <Spin />
                </div>
            );
        }
        return (
            <div className={classes.surveyContainer}>
                <Survey model={model} />
            </div>
        );
    }
);

GuidedSurvey.displayName = "GuidedSurvey";

export default GuidedSurvey;
